import FeatureGates from '@atlaskit/feature-gate-js-client';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	autoSuggestionQuery,
	type AutoSuggestionResult,
} from '@atlassian/search-client/auto-suggestion';

import { type onError } from '../../error-boundary/log-exception';

export type FetchAutoSuggestionParams = {
	primaryProduct: string | undefined;
	cloudId: string | undefined;
	activationId: string | undefined;
};

export const fetchAutoSuggestionResults = (
	query: string,
	params: FetchAutoSuggestionParams,
	onError: onError,
): Promise<{
	results: AutoSuggestionResult[];
	duration: number;
}> => {
	const autoSuggestionFilters = {
		entities: ['query-suggestion.query-suggestion-item'],
		locations: [`ari:cloud:confluence::site/${params.cloudId}`],
	};
	const primaryProduct = params.primaryProduct;

	const fetchData = async (query: string) => {
		// Disable auto-suggestion for Confluence
		if (primaryProduct === 'confluence') {
			return {
				results: [],
				duration: 0,
			};
		}

		// Atlas killswitch
		if (
			primaryProduct === 'townsquare' &&
			!FeatureGates.checkGate('atlas_enable_query_suggestion_dropdown')
		) {
			return {
				results: [],
				duration: 0,
			};
		}

		const headers = buildAtlAttributionHeaders({
			cloudId: params.cloudId,
			activationId: params.activationId,
			product: params.primaryProduct,
		});

		performance.mark('fetchAutoSuggestionResultsStart');

		const response = await autoSuggestionQuery({
			variables: {
				limit: 10,
				filters: autoSuggestionFilters,
				query,
			},
			headers,
		}).catch((error) => {
			onError(error);
		});

		performance.mark('fetchAutoSuggestionResultsEnd');

		const { duration } = performance.measure(
			'fetchAutoSuggestionResultsDuration',
			'fetchAutoSuggestionResultsStart',
			'fetchAutoSuggestionResultsEnd',
		);

		return {
			results: response?.data?.suggest?.suggest?.nodes || [],
			duration,
		};
	};

	return fetchData(query);
};
